<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { PageSection } from '@athena-lib/Page';
  import type { ImageCardsPageSectionBlock } from '@storyblok-types';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import { ButtonLink, type ButtonColor } from '@athena-lib/Button';
  import { castColor } from '@athena-lib/colors';
  import FullwidthCarousel from '@athena-lib/Carousel/FullwidthCarousel.svelte';
  import FullwidthCarouselItem from '@athena-lib/Carousel/FullwidthCarouselItem.svelte';
  import InnerPageSection from '@athena-lib/Page/InnerPageSection.svelte';
  import clsx from 'clsx';
  import { isRichTextEmpty } from '../RichText/types';
  import PageSectionHeading from './shared/PageSectionHeading.svelte';
  import ImageCard from './ImageCard.svelte';
  import { getUrlByLinkType } from '$lib/utilities/storyblok';

  let { blok }: { blok: ImageCardsPageSectionBlock } = $props();

  const layout = $derived(blok.layout || '50:50');
  const cardCount = $derived(layout === '33:33:33' ? 3 : 2);
  const showHeading = $derived(blok.headingText || !isRichTextEmpty(blok.description));
</script>

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
  xSpace="none"
  backgroundColor={blok.backgroundColor ?? 'light'}
  width="full"
>
  <Stack space="xlarge">
    <StackItem class={clsx(blok.swipeInMobile && !showHeading ? 'hidden md:block' : '')}>
      <InnerPageSection>
        <Stack space="xlarge">
          {#if showHeading}
            <StackItem>
              <PageSectionHeading
                headingText={blok.headingText}
                headingTag={blok.headingTag}
                headingSize={blok.headingSize}
                description={blok.description}
                mobileAlignment={blok.mobileAlignment}
                desktopAlignment={blok.desktopAlignment}
              />
            </StackItem>
          {/if}
          <StackItem class={blok.swipeInMobile ? 'hidden md:block' : ''}>
            <Grid columns={{ sm: 1, md: layout === '50:50' ? 2 : 3 }} space="large">
              {#each blok.imageCards ?? [] as imageCard, idx (imageCard._uid)}
                {#if idx < cardCount}
                  <GridItem
                    colSpan={{
                      sm: 1,
                      md:
                        (layout === '66:33' && imageCard === blok.imageCards![0]) ||
                        (layout === '33:66' && imageCard === blok.imageCards![1])
                          ? 2
                          : 1,
                    }}
                  >
                    <ImageCard
                      blok={imageCard}
                      rounded={blok.rounded}
                      labelColor={castColor(blok.labelColor)}
                    />
                  </GridItem>
                {/if}
              {/each}
            </Grid>
          </StackItem>
        </Stack>
      </InnerPageSection>
    </StackItem>
    {#if blok.swipeInMobile}
      <StackItem class="md:hidden">
        <FullwidthCarousel space="small">
          {#each blok.imageCards ?? [] as imageCard}
            <FullwidthCarouselItem>
              <ImageCard
                blok={imageCard}
                rounded={blok.rounded}
                labelColor={castColor(blok.labelColor)}
              />
            </FullwidthCarouselItem>
          {/each}
        </FullwidthCarousel>
      </StackItem>
    {/if}
    {#if blok.ctaButtonText}
      <StackItem class="flex justify-center">
        <ButtonLink
          link={getUrlByLinkType(blok.ctaButtonLink)}
          color={(blok.ctaButtonColor as ButtonColor) ?? 'dark'}
          class={blok.ctaButtonExtraClasses}
        >
          {blok.ctaButtonText}
        </ButtonLink>
      </StackItem>
    {/if}
  </Stack>
</PageSection>
